import { useState } from 'react';
import { extractToken, householdIdFromToken, storeToken } from '../lib/token.ts';
import { isIos } from '../lib/device.ts';

/**
 * Écran affiché lorsqu'aucun token de foyer n'est disponible.
 *
 * # Pourquoi un champ de saisie ici
 *
 * Sur iOS, une PWA installée sur l'écran d'accueil dispose de son **propre
 * stockage**, séparé de celui de Safari. Le token rangé en ouvrant le lien dans
 * Safari n'est donc pas visible depuis l'application installée : celle-ci
 * démarre sur `start_url` (`/`), dans un bac à sable vierge, et le fragment
 * `#t=…` ne peut pas être conservé dans le raccourci puisque iOS utilise le
 * `start_url` du manifeste.
 *
 * Coller le lien est le seul chemin fiable pour transmettre le token à
 * l'application installée. Android ne connaît pas ce problème — le stockage y
 * est partagé — mais le champ sert aussi après un effacement des données.
 *
 * Cela n'affaiblit rien : le token reste la clé, et un token invalide est
 * rejeté par le serveur. Il n'y a toujours aucun endpoint d'énumération, et
 * aucune information n'est exposée à qui ne détient pas déjà un lien valide.
 */
export default function InvalidLink() {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (raw: string) => {
    const token = extractToken(raw);

    if (!token || !householdIdFromToken(token)) {
      setError('Ce lien ne semble pas valide. Colle le lien complet, celui qui contient « #t= ».');
      return;
    }

    storeToken(token);
    // Rechargement plutôt que montage direct : l'amorçage complet (hydratation,
    // moteur de synchronisation, service worker) repart proprement.
    window.location.reload();
  };

  return (
    <main className="empty-screen">
      <h1>Courses</h1>
      <p>Cette application s’ouvre avec le lien du foyer.</p>

      <form
        className="claim"
        onSubmit={(event) => {
          event.preventDefault();
          submit(value);
        }}
      >
        <label className="claim__label" htmlFor="lien">
          Colle ici le lien du foyer
        </label>
        <input
          id="lien"
          className="claim__input"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          placeholder="https://…/#t=…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="url"
        />

        <div className="claim__actions">
          <button type="submit" className="button" disabled={value.trim().length === 0}>
            Ouvrir
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              // Doit rester dans le geste utilisateur : iOS n'accorde l'accès au
              // presse-papiers que sur action explicite.
              void navigator.clipboard
                ?.readText()
                .then((text) => {
                  setValue(text);
                  submit(text);
                })
                .catch(() => setError('Impossible de lire le presse-papiers. Colle le lien à la main.'));
            }}
          >
            Coller
          </button>
        </div>

        {error && (
          <p className="claim__error" role="alert">
            {error}
          </p>
        )}
      </form>

      <p className="empty-screen__hint">
        {isIos() ? (
          <>
            Sur iPhone, l’application installée sur l’écran d’accueil a son propre stockage, séparé de
            Safari : le lien doit y être collé une fois. Copie-le depuis Safari (réglages de
            l’application, « Lien du foyer ») ou depuis le message où tu l’as reçu.
          </>
        ) : (
          <>
            Demande le lien à l’autre appareil : il se trouve dans ses réglages, sous « Lien du foyer ».
          </>
        )}
      </p>
    </main>
  );
}
